import { AttributeSuggestion } from '@fullstory/proto/fs/services/llm-tool';
import { useNotifications } from '@fullstory/react-hooks';
import { fullstorySelectors } from '@fullstory/redux';
import { useRequestQueryOptions } from '@fullstory/requests';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  describeAttributeSuggestionsRequest,
  generateAttributeSuggestionsRequest,
} from './requests';

const { getOrgId } = fullstorySelectors;

export const useAttributeSuggestions = (
  numPages: number,
  numUrlsPerPage: number,
): {
  suggestions: AttributeSuggestion[];
  generateSuggestions: () => void;
  isLoading: boolean;
} => {
  const orgId = useSelector(getOrgId);
  const notifications = useNotifications();

  const { data, error, isLoading, refetch } = useQuery(
    useRequestQueryOptions(
      generateAttributeSuggestionsRequest({
        orgId,
        numPages,
        numUrlsPerPage,
        timeRange: null,
        excludedPageDefIds: [],
      }),
      {
        enabled: false,
      },
    ),
  );

  // TODO: This refactor matches the previous `onError` callback behavior,
  // but it's subtly buggy! In short, errors could be shown more often than
  // expected as this hook is called throughout the app. Read this blog post
  // to understand how to resolve this issue:
  // https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
  useEffect(() => {
    if (error) {
      notifications?.error('Failed to generate attribute suggestions');
    }
  }, [error, notifications]);

  return useMemo(
    () => ({
      suggestions: data?.suggestions ?? [],
      isLoading,
      generateSuggestions: () => refetch(),
    }),
    [data?.suggestions, refetch, isLoading],
  );
};

export const useDescribeAttributeSuggestions = (
  suggestions: AttributeSuggestion[],
): {
  attributeLlmDescription: string;
  isLoading: boolean;
} => {
  const notifications = useNotifications();
  const [enabled, setEnabled] = useState(true);
  const { data, isLoading, error } = useQuery(
    useRequestQueryOptions(
      describeAttributeSuggestionsRequest({
        suggestions,
      }),
      {
        enabled,
      },
    ),
  );

  // For now we only want to ever describe the suggestions once
  useEffect(() => {
    if (data?.response) {
      setEnabled(false);
    }
  }, [data?.response]);

  useEffect(() => {
    if (error) {
      notifications?.error('Failed to generate attribute suggestions');
    }
  }, [error, notifications]);

  return useMemo(
    () => ({
      attributeLlmDescription: data?.response ?? '',
      isLoading,
    }),
    [data?.response, isLoading],
  );
};
